import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, ImagePlus, X, CheckCircle2, Clock3 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";

const when = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "");

const Shot = ({ file, onRemove }) => {
  const [url, setUrl] = useState("");
  useEffect(() => { const u = URL.createObjectURL(file); setUrl(u); return () => URL.revokeObjectURL(u); }, [file]);
  return (
    <div className="relative w-fit" data-testid="change-request-shot-preview">
      <img src={url} alt="Screenshot to send" className="max-h-40 rounded-lg border border-border" />
      <button type="button" onClick={onRemove} aria-label="Remove screenshot" data-testid="change-request-shot-remove"
        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-destructive text-white grid place-items-center hover:bg-destructive/90"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
};

const History = ({ items }) => (
  <div className="space-y-2" data-testid="change-request-history">
    <p className="text-sm font-medium">Your recent requests</p>
    {items.map((r) => (
      <div key={r.id} className="rounded-lg border border-border p-3 text-sm" data-testid={`change-request-item-${r.id}`}>
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{when(r.created_at)}{r.depot_name ? ` · ${r.depot_name}` : ""}</span>
          {r.status === "done"
            ? <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> Done</span>
            : <span className="inline-flex items-center gap-1 text-amber-600 font-semibold"><Clock3 className="w-3.5 h-3.5" /> Sent</span>}
        </div>
        <p className="mt-1 line-clamp-2">{r.message}</p>
      </div>
    ))}
  </div>
);

export const RequestChangeDialog = ({ open, onOpenChange }) => {
  const { company } = useCompany();
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => { if (open) api.changeRequests().then(setItems).catch(() => setItems([])); }, [open]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick a picture (PNG or JPG)"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Screenshot must be under 8 MB"); return; }
    setFile(f);
  };

  const send = async () => {
    if (!message.trim()) { toast.error("Tell us what you'd like changed"); return; }
    setBusy(true);
    try {
      const r = await api.sendChangeRequest(message.trim(), file);
      setItems((x) => [r, ...x]);
      setMessage(""); setFile(null);
      toast.success("Request sent — we'll be in touch");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send your request"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="change-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Request a change</DialogTitle>
          <DialogDescription>
            Ask the Cross Jobs team for a change just for {company?.name || "your company"}. We&apos;ll email you back — changes we make for you don&apos;t affect other companies.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cr-msg">What would you like changed?</Label>
            <Textarea id="cr-msg" rows={5} maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="change-request-message"
              placeholder="e.g. Add a gate code field to our jobs, or put our ABN on the payroll PDF…" />
          </div>
          {file ? <Shot file={file} onRemove={() => setFile(null)} /> : (
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full" data-testid="change-request-attach-btn">
              <ImagePlus className="w-4 h-4 mr-2" /> Attach a screenshot
            </Button>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="change-request-file" />
          {items.length > 0 && <History items={items} />}
        </div>
        <DialogFooter>
          <Button onClick={send} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="change-request-send-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
